import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Card, CardBody, Input, Label } from "reactstrap";
import {
  buildCombinedDocumentText,
  parseDocumentUrlInput,
  readDocumentFiles,
  readDocumentUrls,
} from "../utils/documentTextExtractor";
import { getAIProviderManager } from "../utils/aiProviderFactory";
import {
  buildPerspectiveRegenerationPrompt,
  runMissingPerspectiveWorkflow,
} from "../utils/missingPerspectiveAnalysis";
import {
  PRODUCT_ANALYTICS_EVENTS,
  getBooleanFlag,
  getSizeBucket,
} from "../utils/analyticsEvents";
import { trackProductEvent } from "../utils/productAnalytics";
import MissingPerspectiveUploadField from "./MissingPerspectiveUploadField";
import TLLogoLoader from "./TLLogoLoader";
import "./MissingPerspectiveAnalyzer.css";

const SUPPORTED_UPLOAD_EXTENSIONS = new Set([
  "txt",
  "md",
  "csv",
  "json",
  "html",
  "htm",
  "pdf",
  "docx",
]);

const formatBytes = (value = 0) => {
  if (!Number.isFinite(value) || value <= 0) return "";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${Math.round(value / 102.4) / 10} KB`;
  return `${Math.round(value / 104857.6) / 10} MB`;
};

const getFileExtension = (file) =>
  String(file?.name || "")
    .split(".")
    .pop()
    ?.toLowerCase() || "";

const isSupportedUploadFile = (file) =>
  SUPPORTED_UPLOAD_EXTENSIONS.has(getFileExtension(file));

const buildUnsupportedFilesMessage = (files = []) => {
  const names = (Array.isArray(files) ? files : [])
    .map((file) => file?.name || "Unnamed file")
    .filter(Boolean);
  if (!names.length) return "";
  return `Unsupported file type: ${names.join(", ")}. Upload TXT, MD, CSV, JSON, HTML, PDF, or DOCX files.`;
};

const URL_ACCESS_RESTRICTION_PATTERN =
  /blocks automated readers|blocked automated access|rate-limiting requests|login|paywall|bot protection/i;

const buildUrlImportNotice = (documents = []) => {
  const failedCount = (Array.isArray(documents) ? documents : []).filter(
    (document) => document?.extractionError
  ).length;
  if (!failedCount) {
    return "";
  }

  const totalCount = Array.isArray(documents) ? documents.length : 0;
  const successCount = Math.max(0, totalCount - failedCount);
  const hasAccessRestrictedFailures = (Array.isArray(documents) ? documents : []).some(
    (document) =>
      URL_ACCESS_RESTRICTION_PATTERN.test(String(document?.extractionError || ""))
  );

  if (!successCount) {
    return hasAccessRestrictedFailures
      ? "Those URLs could not be imported. Some sites block automated readers or require login. Try a public PDF, DOCX, or text URL, or paste the text directly."
      : "We could not extract readable text from those URLs. Try a public PDF, DOCX, or text URL, or paste the text directly.";
  }

  if (hasAccessRestrictedFailures) {
    return `Added ${successCount} URL(s). ${failedCount} URL(s) could not be imported because the source blocks automated readers or requires login.`;
  }

  return `Added ${successCount} URL(s). ${failedCount} URL(s) could not be read.`;
};

const getDomainLabel = (url = "") => {
  try {
    return new URL(url).hostname.replace(/^www\./i, "");
  } catch (error) {
    return "";
  }
};

const getYouTubeTarget = (value = {}) => {
  const url = typeof value === "string" ? value : value?.url || "";
  const directVideoId =
    typeof value === "object" && value?.videoId ? String(value.videoId) : "";
  const videoMatch =
    url.match(/[?&]v=([A-Za-z0-9_-]+)/) ||
    url.match(/youtu\.be\/([A-Za-z0-9_-]+)/);
  const videoId = directVideoId || (videoMatch ? videoMatch[1] : "");
  return {
    videoId,
    isYouTube: Boolean(videoId),
  };
};

const getYouTubeThumbs = (value = {}) => {
  const { videoId } = getYouTubeTarget(value);
  if (!videoId) return [];
  return [
    `https://i.ytimg.com/vi_webp/${videoId}/hqdefault.webp`,
    `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`,
    `https://img.youtube.com/vi/${videoId}/default.jpg`,
  ];
};

const buildYouTubeEmbedUrl = (value = {}) => {
  const { videoId } = getYouTubeTarget(value);
  return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : "";
};

const mergeDocuments = (currentDocuments = [], nextDocuments = []) => {
  const seen = new Set(currentDocuments.map((document) => document.id));
  const merged = [...currentDocuments];
  nextDocuments.forEach((document) => {
    if (seen.has(document.id)) return;
    seen.add(document.id);
    merged.push(document);
  });
  return merged;
};

const getDocumentSignature = (items = []) =>
  (Array.isArray(items) ? items : [])
    .map(
      (document) =>
        `${document?.id || document?.name || "document"}::${document?.content || ""}`
    )
    .join("|");

const MissingPerspectiveAnalyzer = ({
  title = "Missing Voices Analysis",
  description = "",
  eyebrow = "Voices Lab",
  inputLabel = "Text to analyze",
  textPlaceholder = "Paste text here",
  analyzeButtonLabel = "Analyze Missing Voices",
  rerunButtonLabel = "Re-run Analysis",
  sourceLabel = "source text",
  initialText = "",
  initialDocuments = null,
  editableText = true,
  allowUploads = true,
  autoAnalyze = false,
  className = "",
  onApplyRecommendations = null,
  applyPrompt = "Would you like to regenerate the lesson plan so it includes the missing voices and resources below?",
  applyButtonLabel = "Regenerate Lesson Plan With These Voices",
}) => {
  const [text, setText] = useState(initialText || "");
  const normalizedInitialDocuments = useMemo(
    () => (Array.isArray(initialDocuments) ? initialDocuments : []),
    [initialDocuments]
  );
  const initialDocumentsSignature = useMemo(
    () => getDocumentSignature(normalizedInitialDocuments),
    [normalizedInitialDocuments]
  );
  const [documents, setDocuments] = useState(normalizedInitialDocuments);
  const [analysis, setAnalysis] = useState(null);
  const [resources, setResources] = useState([]);
  const [videos, setVideos] = useState([]);
  const [analysisError, setAnalysisError] = useState("");
  const [searchError, setSearchError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [urlInput, setUrlInput] = useState("");
  const [urlLoading, setUrlLoading] = useState(false);
  const [uploadNotice, setUploadNotice] = useState("");
  const [uploadNoticeTone, setUploadNoticeTone] = useState("warning");
  const [applyLoading, setApplyLoading] = useState(false);
  const [applyNotice, setApplyNotice] = useState("");
  const [applyNoticeTone, setApplyNoticeTone] = useState("info");
  const [lastAnalyzedSignature, setLastAnalyzedSignature] = useState("");
  const [videoThumbFailures, setVideoThumbFailures] = useState({});
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isDropActive, setIsDropActive] = useState(false);
  const autoAnalyzeSignatureRef = useRef("");
  const dropDepthRef = useRef(0);
  const hasHeaderContent = Boolean(eyebrow || title || description);

  useEffect(() => {
    if (!editableText) {
      setText(initialText || "");
    }
  }, [editableText, initialText]);

  useEffect(() => {
    if (!allowUploads) {
      setDocuments((prev) => {
        const prevSignature = getDocumentSignature(prev);
        if (prevSignature === initialDocumentsSignature) {
          return prev;
        }
        return normalizedInitialDocuments;
      });
    }
  }, [allowUploads, initialDocumentsSignature, normalizedInitialDocuments]);

  const combinedText = useMemo(
    () => buildCombinedDocumentText({ text, documents }),
    [documents, text]
  );
  const currentSignature = combinedText.trim();
  const hasUsableInput = Boolean(currentSignature);
  const hasPendingUrlInput = Boolean(urlInput.trim());
  const isStale = Boolean(
    analysis && lastAnalyzedSignature && lastAnalyzedSignature !== currentSignature
  );
  const canApplyRecommendations = Boolean(
    onApplyRecommendations &&
      analysis &&
      (analysis.missingPerspectives.length > 0 ||
        resources.length > 0 ||
        videos.length > 0)
  );
  const loadingState = uploading
    ? {
        heading: "Reading your files",
        body: "Extracting text from uploads before the voices audit begins.",
      }
    : urlLoading
      ? {
          heading: "Reading your URL",
          body: "Fetching the source and extracting readable text before the voices audit begins.",
        }
    : loading
      ? {
          heading: analysis ? "Refreshing the voices audit" : "Analyzing missing voices",
          body: "Checking whose voices are centered, then surfacing resources and relevant videos.",
        }
      : null;
  const isGenerating = uploading || urlLoading || loading || applyLoading;

  const handleAnalyze = useCallback(async () => {
    let analysisDocuments = documents;
    let nextSignature = currentSignature;

    if (allowUploads && hasPendingUrlInput) {
      const pendingUrls = parseDocumentUrlInput(urlInput);
      if (!pendingUrls.length) {
        setUploadNoticeTone("warning");
        setUploadNotice("Enter a valid http(s) URL.");
        return;
      }

      setUrlLoading(true);
      setAnalysisError("");
      setUploadNotice("");

      try {
        const nextDocuments = await readDocumentUrls(pendingUrls);
        analysisDocuments = mergeDocuments(documents, nextDocuments);
        setDocuments(analysisDocuments);
        nextSignature = buildCombinedDocumentText({
          text,
          documents: analysisDocuments,
        }).trim();

        const failedCount = nextDocuments.filter(
          (document) => document.extractionError
        ).length;
        if (failedCount > 0) {
          setUploadNoticeTone("warning");
          setUploadNotice(buildUrlImportNotice(nextDocuments));
        }

        setUrlInput("");
      } finally {
        setUrlLoading(false);
      }
    }

    if (!nextSignature) {
      setAnalysisError(
        "Paste text, add a URL, or upload readable files before analyzing."
      );
      return;
    }

    setLoading(true);
    setAnalysisError("");
    setSearchError("");
    setApplyNotice("");

    try {
      const result = await runMissingPerspectiveWorkflow({
        text,
        documents: analysisDocuments,
        sourceLabel,
      });
      setAnalysis(result.analysis);
      setResources(result.resources);
      setVideos(result.videos || []);
      setSearchError(result.searchError || "");
      setLastAnalyzedSignature(nextSignature);
      trackProductEvent(PRODUCT_ANALYTICS_EVENTS.ANALYSIS_COMPLETED, {
        document_count: analysisDocuments.length,
        has_inline_text: getBooleanFlag(text.trim()),
        input_length_bucket: getSizeBucket(nextSignature.length),
        is_repeat_analysis:
          Boolean(lastAnalyzedSignature) && lastAnalyzedSignature !== nextSignature,
        missing_perspective_count: Array.isArray(
          result.analysis?.missingPerspectives
        )
          ? result.analysis.missingPerspectives.length
          : 0,
        resource_count: Array.isArray(result.resources)
          ? result.resources.length
          : 0,
        source_label: sourceLabel || null,
        used_url_import: getBooleanFlag(hasPendingUrlInput),
        video_count: Array.isArray(result.videos) ? result.videos.length : 0,
      });
    } catch (error) {
      console.error("Missing voices analysis failed:", error);
      setAnalysisError(
        error?.message || "Unable to analyze missing voices right now."
      );
    } finally {
      setLoading(false);
    }
  }, [
    allowUploads,
    currentSignature,
    documents,
    hasPendingUrlInput,
    lastAnalyzedSignature,
    sourceLabel,
    text,
    urlInput,
  ]);

  const handleVideoThumbError = (videoId) => {
    if (!videoId) return;
    setVideoThumbFailures((prev) => ({
      ...prev,
      [videoId]: (prev[videoId] || 0) + 1,
    }));
  };

  useEffect(() => {
    if (!autoAnalyze || !currentSignature || loading) return;
    if (autoAnalyzeSignatureRef.current === currentSignature) return;
    autoAnalyzeSignatureRef.current = currentSignature;
    void handleAnalyze();
  }, [autoAnalyze, currentSignature, handleAnalyze, loading]);

  const handleSelectedFiles = useCallback(async (incomingFiles = []) => {
    const files = Array.from(incomingFiles || []);
    if (!files.length) return;
    const supportedFiles = files.filter(isSupportedUploadFile);
    const unsupportedFiles = files.filter((file) => !isSupportedUploadFile(file));

    if (unsupportedFiles.length > 0) {
      setUploadNoticeTone("warning");
      setUploadNotice(buildUnsupportedFilesMessage(unsupportedFiles));
    } else {
      setUploadNotice("");
    }

    if (!supportedFiles.length) {
      return;
    }

    setUploading(true);
    setAnalysisError("");
    try {
      const nextDocuments = await readDocumentFiles(supportedFiles);
      setDocuments((prev) => mergeDocuments(prev, nextDocuments));
    } finally {
      setUploading(false);
    }
  }, []);

  const handleFileInput = async (event) => {
    try {
      await handleSelectedFiles(event.target.files || []);
    } finally {
      event.target.value = "";
    }
  };

  const handleDragEnter = (event) => {
    if (!allowUploads) return;
    const types = Array.from(event?.dataTransfer?.types || []);
    if (!types.includes("Files")) return;
    event.preventDefault();
    dropDepthRef.current += 1;
    setIsDropActive(true);
  };

  const handleDragOver = (event) => {
    if (!allowUploads) return;
    event.preventDefault();
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragLeave = (event) => {
    if (!allowUploads) return;
    const types = Array.from(event?.dataTransfer?.types || []);
    if (types.length > 0 && !types.includes("Files")) return;
    event.preventDefault();
    dropDepthRef.current = Math.max(0, dropDepthRef.current - 1);
    if (dropDepthRef.current === 0) {
      setIsDropActive(false);
    }
  };

  const handleDrop = async (event) => {
    if (!allowUploads) return;
    event.preventDefault();
    dropDepthRef.current = 0;
    setIsDropActive(false);
    await handleSelectedFiles(event.dataTransfer?.files || []);
  };

  const handleRemoveDocument = (documentId) => {
    setDocuments((prev) => prev.filter((document) => document.id !== documentId));
  };

  const handleApplyRecommendations = async () => {
    if (!canApplyRecommendations) return;
    setApplyLoading(true);
    setApplyNotice("");
    try {
      const prompt = buildPerspectiveRegenerationPrompt({
        lessonPlan: text,
        analysis,
        resources,
        videos,
      });
      const aiManager = getAIProviderManager();
      const result = await aiManager.generate(prompt);
      const nextLessonPlan = String(result?.content || "").trim();
      if (!nextLessonPlan) {
        throw new Error("Perspective regeneration returned an empty lesson plan.");
      }
      await onApplyRecommendations({
        lessonPlan: nextLessonPlan,
        analysis,
        resources,
        videos,
      });
      trackProductEvent(PRODUCT_ANALYTICS_EVENTS.RECOMMENDATIONS_APPLIED, {
        missing_perspective_count: Array.isArray(
          analysis?.missingPerspectives
        )
          ? analysis.missingPerspectives.length
          : 0,
        resource_count: Array.isArray(resources) ? resources.length : 0,
        video_count: Array.isArray(videos) ? videos.length : 0,
      });
      setApplyNotice("Updated the lesson plan with the missing voices.");
      setApplyNoticeTone("success");
    } catch (error) {
      console.error("Perspective regeneration failed:", error);
      setApplyNotice(
        error?.message ||
          "Unable to regenerate the lesson plan with those voices."
      );
      setApplyNoticeTone("danger");
    } finally {
      setApplyLoading(false);
    }
  };

  return (
    <section
      className={`missing-perspective-analyzer missing-perspective-analyzer__body ${className}`.trim()}
    >
      {hasHeaderContent ? (
        <div className="missing-perspective-analyzer__header">
          {eyebrow ? (
            <div className="missing-perspective-analyzer__eyebrow">
              <i className="fa fa-compass" aria-hidden="true" />
              <span>{eyebrow}</span>
            </div>
          ) : null}
          {title ? <h2 className="missing-perspective-analyzer__title">{title}</h2> : null}
          {description ? (
            <p className="missing-perspective-analyzer__description">{description}</p>
          ) : null}
        </div>
      ) : null}

      <div className="missing-perspective-analyzer__content">
        <Card
          className={`missing-perspective-analyzer__input-card${
            isDropActive ? " is-drag-over" : ""
          }${
            isGenerating ? " is-generating" : ""
          }`}
          onDragEnter={allowUploads ? handleDragEnter : undefined}
          onDragOver={allowUploads ? handleDragOver : undefined}
          onDragLeave={allowUploads ? handleDragLeave : undefined}
          onDrop={allowUploads ? (event) => void handleDrop(event) : undefined}
        >
          <CardBody className="p-0">
            {allowUploads && isDropActive ? (
              <div
                className="missing-perspective-analyzer__drop-overlay"
                aria-hidden="true"
              >
                <div className="missing-perspective-analyzer__drop-overlay-copy">
                  <div className="missing-perspective-analyzer__drop-overlay-icon">
                    <i className="fa fa-cloud-upload" aria-hidden="true" />
                  </div>
                  <div className="missing-perspective-analyzer__drop-overlay-title">
                    Drop files to upload
                  </div>
                  <div className="missing-perspective-analyzer__drop-overlay-subtext">
                    TXT, MD, CSV, JSON, HTML, PDF, and DOCX files are supported.
                  </div>
                </div>
              </div>
            ) : null}

            {editableText ? (
              <>
                <div className="missing-perspective-analyzer__input-intro">
                  <div className="missing-perspective-analyzer__input-copy">
                    <Label
                      className="missing-perspective-analyzer__input-label"
                      htmlFor="missing-perspective-text-input"
                    >
                      {inputLabel}
                    </Label>
                    <p className="missing-perspective-analyzer__input-description">
                      Paste your text, upload a document, or add a link for
                      analysis.
                    </p>
                  </div>

                  {allowUploads ? (
                    <div className="missing-perspective-analyzer__input-intro-actions">
                      <MissingPerspectiveUploadField
                        className="missing-perspective-analyzer__upload-field"
                        uploading={uploading}
                        generating={isGenerating}
                        onChange={handleFileInput}
                      />
                    </div>
                  ) : null}
                </div>
                <Input
                  id="missing-perspective-text-input"
                  className="missing-perspective-analyzer__textarea"
                  type="textarea"
                  value={text}
                  onChange={(event) => setText(event.target.value)}
                  placeholder={textPlaceholder}
                />
              </>
            ) : (
              <div className="missing-perspective-analyzer__source-note">
                Reviewing the current lesson plan{documents.length > 0 ? ` plus ${documents.length} added source(s)` : ""}.
              </div>
            )}

            <div className="missing-perspective-analyzer__toolbar">
              {allowUploads ? (
                <div className="missing-perspective-analyzer__url-row">
                  <Label
                    className="missing-perspective-analyzer__url-label"
                    htmlFor="missing-perspective-url-input"
                  >
                    Source URL
                  </Label>
                  <Input
                    id="missing-perspective-url-input"
                    type="url"
                    value={urlInput}
                    placeholder="https://example.com/article"
                    className="missing-perspective-analyzer__url-input"
                    onChange={(event) => setUrlInput(event.target.value)}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") {
                        event.preventDefault();
                        void handleAnalyze();
                      }
                    }}
                    disabled={uploading || urlLoading || loading}
                  />
                </div>
              ) : null}

              <div className="missing-perspective-analyzer__toolbar-actions">
                <Button
                  color="primary"
                  className="missing-perspective-analyzer__analyze-button"
                  onClick={() => void handleAnalyze()}
                  disabled={
                    (!hasUsableInput && !hasPendingUrlInput) ||
                    loading ||
                    uploading ||
                    urlLoading
                  }
                >
                  {loading ? (
                    <>
                      <TLLogoLoader
                        label={null}
                        size={16}
                        layout="inline"
                        className="missing-perspective-analyzer__button-loader"
                      />
                      Analyzing...
                    </>
                  ) : analysis
                    ? rerunButtonLabel
                    : analyzeButtonLabel}
                </Button>
              </div>

              {isStale ? (
                <div className="missing-perspective-analyzer__stale-note">
                  Source text changed since the last analysis.
                </div>
              ) : null}
            </div>

            {allowUploads ? (
              <div className="missing-perspective-analyzer__upload-note">
                Paste text, enter a public source URL, or drop files anywhere in
                this card, then click Analyze. Public web pages, PDFs, DOCX
                files, and plain-text links work best. Login walls and
                bot-protected pages usually need copy/paste instead.
              </div>
            ) : null}

            {documents.length > 0 ? (
              <div className="missing-perspective-analyzer__document-list">
                {documents.map((document) => (
                  <div
                    key={document.id}
                    className="missing-perspective-analyzer__document-chip"
                  >
                    <div>
                      <div className="missing-perspective-analyzer__document-name">
                        {document.name}
                      </div>
                      <div className="missing-perspective-analyzer__document-meta">
                        {[formatBytes(document.size), document.type]
                          .filter(Boolean)
                          .join(" • ")}
                      </div>
                      {document.sourceUrl ? (
                        <a
                          href={document.sourceUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="missing-perspective-analyzer__document-link"
                        >
                          {getDomainLabel(document.sourceUrl) || document.sourceUrl}
                        </a>
                      ) : null}
                      {document.extractionError ? (
                        <div className="text-danger small mt-2">
                          {document.extractionError}
                        </div>
                      ) : null}
                    </div>
                    {allowUploads ? (
                      <Button
                        color="link"
                        className="p-0 text-danger"
                        onClick={() => handleRemoveDocument(document.id)}
                      >
                        Remove
                      </Button>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : null}
          </CardBody>
        </Card>

        {uploadNotice ? (
          <Alert color={uploadNoticeTone} className="mt-3 mb-0" fade={false}>
            {uploadNotice}
          </Alert>
        ) : null}

        {analysisError ? (
          <Alert color="danger" className="mt-3 mb-0" fade={false}>
            {analysisError}
          </Alert>
        ) : null}

        {loadingState ? (
          <div
            className="missing-perspective-analyzer__loading-card"
            aria-live="polite"
          >
            <TLLogoLoader
              label={null}
              size={34}
              className="missing-perspective-analyzer__loading-logo"
            />
            <div className="missing-perspective-analyzer__loading-copy">
              <div className="missing-perspective-analyzer__loading-title">
                {loadingState.heading}
              </div>
              <p className="missing-perspective-analyzer__loading-body">
                {loadingState.body}
              </p>
            </div>
          </div>
        ) : null}

        {analysis ? (
          <>
            <div className="missing-perspective-analyzer__results-grid">
              <div className="missing-perspective-analyzer__results-card">
                <h3 className="missing-perspective-analyzer__section-title">
                  <i className="fa fa-eye" aria-hidden="true" />
                  <span>Whose Voices The Text Centers</span>
                </h3>
                <p className="missing-perspective-analyzer__summary">
                  {analysis.summary || "The analysis completed, but the summary was brief."}
                </p>
                <div className="missing-perspective-analyzer__perspective-list mt-3">
                  {analysis.representedPerspectives.length > 0 ? (
                    analysis.representedPerspectives.map((item) => (
                      <div
                        key={`represented-${item.label}`}
                        className="missing-perspective-analyzer__perspective-item"
                      >
                        <div className="missing-perspective-analyzer__perspective-label">
                          <i className="fa fa-check-circle text-success" aria-hidden="true" />
                          <span>{item.label}</span>
                        </div>
                        {item.evidence ? (
                          <p className="missing-perspective-analyzer__perspective-copy">
                            Evidence: {item.evidence}
                          </p>
                        ) : null}
                        {item.note ? (
                          <p className="missing-perspective-analyzer__perspective-copy">
                            {item.note}
                          </p>
                        ) : null}
                      </div>
                    ))
                  ) : (
                    <div className="missing-perspective-analyzer__perspective-item">
                      The analysis could not identify clearly centered voices.
                    </div>
                  )}
                </div>
              </div>

              <div className="missing-perspective-analyzer__results-card">
                <h3 className="missing-perspective-analyzer__section-title">
                  <i className="fa fa-search" aria-hidden="true" />
                  <span>Whose Voices Feel Missing</span>
                </h3>
                {analysis.missingPerspectives.length > 0 ? (
                  <div className="missing-perspective-analyzer__perspective-list">
                    {analysis.missingPerspectives.map((item) => (
                      <div
                        key={`missing-${item.label}`}
                        className="missing-perspective-analyzer__perspective-item"
                      >
                        <div className="missing-perspective-analyzer__perspective-label">
                          <i className="fa fa-exclamation-circle text-warning" aria-hidden="true" />
                          <span>{item.label}</span>
                        </div>
                        {item.whyItMatters ? (
                          <p className="missing-perspective-analyzer__perspective-copy">
                            {item.whyItMatters}
                          </p>
                        ) : null}
                        <div className="missing-perspective-analyzer__resource-meta">
                          {item.searchQuery ? (
                            <Badge color="warning" pill>
                              {item.searchQuery}
                            </Badge>
                          ) : null}
                          {item.resourceFocus ? (
                            <Badge color="secondary" pill>
                              {item.resourceFocus}
                            </Badge>
                          ) : null}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="missing-perspective-analyzer__perspective-item">
                    No major missing voices were flagged in the current text.
                  </div>
                )}
              </div>
            </div>

            {analysis.caution ? (
              <Alert color="warning" className="mt-3 mb-0" fade={false}>
                {analysis.caution}
              </Alert>
            ) : null}

            <div className="missing-perspective-analyzer__results-card mt-3">
              <h3 className="missing-perspective-analyzer__section-title">
                <i className="fa fa-globe" aria-hidden="true" />
                <span>Resources To Broaden The Frame</span>
              </h3>

              {searchError ? (
                <Alert color="warning" className="mb-3" fade={false}>
                  {searchError}
                </Alert>
              ) : null}

              {resources.length > 0 ? (
                <div className="missing-perspective-analyzer__resource-list">
                  {resources.map((resource) => (
                    <div
                      key={`${resource.url}-${resource.query}`}
                      className="missing-perspective-analyzer__resource-card"
                    >
                      <a
                        className="missing-perspective-analyzer__resource-title"
                        href={resource.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        <span>{resource.title}</span>
                        <i className="fa fa-external-link" aria-hidden="true" />
                      </a>
                      {resource.summary || resource.snippet ? (
                        <p className="missing-perspective-analyzer__resource-snippet">
                          {resource.summary || resource.snippet}
                        </p>
                      ) : null}
                      <div className="missing-perspective-analyzer__resource-meta">
                        {resource.query ? (
                          <Badge color="info" pill>
                            {resource.query}
                          </Badge>
                        ) : null}
                        {resource.originalLanguageLabel &&
                        resource.originalLanguageCode &&
                        resource.originalLanguageCode !==
                          (resource.summaryLanguageCode || "") ? (
                          <Badge color="secondary" pill>
                            Original: {resource.originalLanguageLabel}
                          </Badge>
                        ) : null}
                        {getDomainLabel(resource.url) ? (
                          <Badge color="light" pill>
                            {getDomainLabel(resource.url)}
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="missing-perspective-analyzer__resource-card">
                  No supporting resources were surfaced yet. Try editing the text or
                  re-running the analysis with more source material.
                </div>
              )}
            </div>

            <div className="missing-perspective-analyzer__results-card mt-3">
              <h3 className="missing-perspective-analyzer__section-title">
                <i className="fa fa-play-circle" aria-hidden="true" />
                <span>Relevant YouTube Videos</span>
              </h3>

              {videos.length > 0 ? (
                <div className="lesson-plan-video-grid mt-3">
                  {videos.map((video) => {
                    const thumbOptions = [
                      video.thumbnailUrl,
                      ...getYouTubeThumbs(video),
                      "/favicon.ico",
                    ].filter(Boolean);
                    const thumbIndex = Math.min(
                      videoThumbFailures[video.videoId] || 0,
                      Math.max(thumbOptions.length - 1, 0)
                    );
                    const thumbSrc = thumbOptions[thumbIndex] || "/favicon.ico";

                    return (
                      <button
                        key={`${video.videoId}-${video.query || "video"}`}
                        type="button"
                        className="lesson-plan-video-link"
                        onClick={() => setSelectedVideo(video)}
                        aria-label={video.title || "Open video"}
                      >
                        <div className="lesson-plan-video-card missing-perspective-analyzer__video-card">
                          <img
                            src={thumbSrc}
                            alt={video.title || "Video thumbnail"}
                            onError={() => handleVideoThumbError(video.videoId)}
                            className="lesson-plan-video-thumb"
                            loading="lazy"
                          />
                          <div className="lesson-plan-video-label">
                            {video.title}
                          </div>
                          <div className="missing-perspective-analyzer__video-meta">
                            {video.channelTitle || "YouTube"}
                          </div>
                          {video.query ? (
                            <div className="missing-perspective-analyzer__video-query">
                              Surfaced for: {video.query}
                            </div>
                          ) : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <div className="missing-perspective-analyzer__resource-card">
                  No relevant YouTube videos were surfaced yet. Re-run the
                  analysis after changing the source text if you want a different
                  set of suggestions.
                </div>
              )}
            </div>

            {canApplyRecommendations ? (
              <div className="missing-perspective-analyzer__apply-card">
                <div className="fw-semibold">{applyPrompt}</div>
                <div className="missing-perspective-analyzer__apply-actions">
                  <Button
                    color="primary"
                    onClick={() => void handleApplyRecommendations()}
                    disabled={applyLoading}
                  >
                    {applyLoading ? (
                      <>
                        <TLLogoLoader
                          label={null}
                          size={16}
                          layout="inline"
                          className="missing-perspective-analyzer__button-loader"
                        />
                        Regenerating...
                      </>
                    ) : (
                      applyButtonLabel
                    )}
                  </Button>
                  <Button
                    color="secondary"
                    outline
                    onClick={() => {
                      setApplyNotice("Keeping the current lesson plan unchanged.");
                      setApplyNoticeTone("info");
                    }}
                    disabled={applyLoading}
                  >
                    Keep Current Plan
                  </Button>
                </div>
                {applyNotice ? (
                  <Alert color={applyNoticeTone} className="mt-3 mb-0" fade={false}>
                    {applyNotice}
                  </Alert>
                ) : null}
              </div>
            ) : null}
          </>
        ) : null}
      </div>

      {selectedVideo ? (
        <div
          className="video-modal-backdrop"
          role="dialog"
          aria-modal="true"
          onClick={(event) => {
            if (event.target === event.currentTarget) {
              setSelectedVideo(null);
            }
          }}
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              setSelectedVideo(null);
            }
          }}
        >
          <div className="video-modal" onClick={(event) => event.stopPropagation()}>
            <div className="video-modal-header">
              <div className="video-modal-title">
                {selectedVideo.title || "Video"}
              </div>
              <button
                type="button"
                className="video-modal-close"
                onClick={() => setSelectedVideo(null)}
                aria-label="Close video"
              >
                ×
              </button>
            </div>
            <div className="video-modal-player">
              <iframe
                title={selectedVideo.title || "YouTube video player"}
                src={buildYouTubeEmbedUrl(selectedVideo)}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
            <div className="video-modal-footer">
              <a
                href={selectedVideo.url}
                target="_blank"
                rel="noreferrer"
                className="video-modal-link"
              >
                Open on YouTube
              </a>
            </div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

export default MissingPerspectiveAnalyzer;
